import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { supabase } from "@/lib/supabaseClient";

const AuthContext = createContext({
    session: null,
    user: null,
    profile: null,
    loading: true,
    signUpWithEmail: async () => {},
    signInWithEmail: async () => {},
    signInWithGoogle: async () => {},
    signOut: async () => {},
    sendPasswordResetEmail: async () => {},
    updatePassword: async () => {},
    updateProfile: async () => {},
    refreshProfile: async () => {},
});

// ---------------------------------------------------------------------------
// Stateless auth actions — no component state involved, so they live at
// module scope (stable references, no useCallback needed).
// ---------------------------------------------------------------------------

async function signUpWithEmail({ email, password, fullName }) {
    const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
            data: { full_name: fullName },
            emailRedirectTo: `${window.location.origin}/login`,
        },
    });
    if (error) throw error;
    return data;
}

async function signInWithEmail({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });
    if (error) throw error;
    // touch last_login
    if (data?.user) {
        await supabase
            .from("profiles")
            .update({ last_login: new Date().toISOString() })
            .eq("id", data.user.id);
    }
    return data;
}

async function signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) throw error;
    return data;
}

async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
}

async function sendPasswordResetEmail(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
}

async function updatePassword(newPassword) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
}

// ---------------------------------------------------------------------------
// Provider — holds session/user/profile state only.
// ---------------------------------------------------------------------------

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchProfile = useCallback(async (userId) => {
        if (!userId) {
            setProfile(null);
            return;
        }
        const { data, error } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .maybeSingle();
        if (error) {
            console.error("[Auth] fetchProfile error", error);
            setProfile(null);
            return;
        }
        setProfile(data);
    }, []);

    useEffect(() => {
        let mounted = true;
        const init = async () => {
            const {
                data: { session: initialSession },
            } = await supabase.auth.getSession();
            if (!mounted) return;
            setSession(initialSession);
            setUser(initialSession?.user ?? null);
            if (initialSession?.user) await fetchProfile(initialSession.user.id);
            setLoading(false);
        };
        init();

        const { data: sub } = supabase.auth.onAuthStateChange(
            async (_event, newSession) => {
                setSession(newSession);
                setUser(newSession?.user ?? null);
                if (newSession?.user) await fetchProfile(newSession.user.id);
                else setProfile(null);
            },
        );

        return () => {
            mounted = false;
            sub?.subscription?.unsubscribe?.();
        };
    }, [fetchProfile]);

    const updateProfile = useCallback(
        async (updates) => {
            if (!user) throw new Error("Not authenticated");
            const { error } = await supabase
                .from("profiles")
                .update(updates)
                .eq("id", user.id);
            if (error) throw error;
            if (updates.full_name) {
                await supabase.auth.updateUser({
                    data: { full_name: updates.full_name },
                });
            }
            await fetchProfile(user.id);
        },
        [user, fetchProfile],
    );

    const refreshProfile = useCallback(async () => {
        if (user) await fetchProfile(user.id);
    }, [user, fetchProfile]);

    const value = useMemo(
        () => ({
            session,
            user,
            profile,
            loading,
            signUpWithEmail,
            signInWithEmail,
            signInWithGoogle,
            signOut,
            sendPasswordResetEmail,
            updatePassword,
            updateProfile,
            refreshProfile,
        }),
        [session, user, profile, loading, updateProfile, refreshProfile],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
